import {
  ConfigValues,
  CalculationValues,
  PartsVariables,
  LaborVariables,
  CalculationResults,
} from '../types';

/**
 * Simula un POST request con el JSON actualizado
 * @param data - Datos completos del formulario
 */
export function simulatePost(data: {
  configValues: ConfigValues;
  inputValues?: CalculationValues;
  conditionalVariables?: {
    partsVariables: PartsVariables;
    laborVariables: LaborVariables;
  };
  calculations?: CalculationResults;
  timestamp?: string;
}): void {
  const timestamp = new Date().toISOString();

  const postData = {
    timestamp,
    configValues: data.configValues,
    ...(data.inputValues && { inputValues: data.inputValues }),
    ...(data.conditionalVariables && {
      conditionalVariables: data.conditionalVariables,
    }),
    ...(data.calculations && { calculations: data.calculations }),
    summary: data.calculations
      ? {
          totalPartsLoss: data.calculations.totalPartsLoss,
          totalLaborLoss: data.calculations.totalLaborLoss,
          totalLossAmount: data.calculations.totalLossAmount,
          finalIndemnityAmount: data.calculations.finalIndemnityAmount,
        }
      : undefined,
    breakdown: data.calculations
      ? {
          parts: {
            netParts: data.inputValues?.netParts || 0,
            discount: data.configValues.discount,
            vat: data.configValues.vat,
            netTotalParts: data.calculations.netTotalParts,
            preliminaryPartsLoss: data.calculations.preliminaryPartsLoss,
            hiddenDamagePartsWithVAT:
              data.calculations.hiddenDamagePartsWithVAT,
            totalPartsLoss: data.calculations.totalPartsLoss,
            conditionalDiscount: data.conditionalVariables
              ? `${data.conditionalVariables.partsVariables.workshop} + ${data.conditionalVariables.partsVariables.company} = ${data.configValues.discount}%`
              : `${data.configValues.discount}%`,
          },
          labor: {
            bodyworkHours: data.inputValues?.bodyworkHours || 0,
            paintHours: data.inputValues?.paintHours || 0,
            mechanicalHours: data.inputValues?.mechanicalHours || 0,
            bodyworkRate: data.configValues.bodyworkRate,
            paintRate: data.configValues.paintRate,
            mechanicalRate: data.configValues.mechanicalRate,
            preliminaryLabor: data.calculations.preliminaryLabor,
            hiddenDamageLaborWithVAT:
              data.calculations.hiddenDamageLaborWithVAT,
            totalLaborLoss: data.calculations.totalLaborLoss,
            conditionalLaborCost: data.conditionalVariables
              ? `${data.conditionalVariables.laborVariables.workshop} + ${data.conditionalVariables.laborVariables.type} = ₡${data.configValues.bodyworkRate}`
              : `₡${data.configValues.bodyworkRate}`,
          },
          adjustments: {
            underinsurance: data.configValues.underinsurance,
            depreciation: data.configValues.depreciation,
            deductible: data.configValues.deductible,
          },
        }
      : undefined,
  };

  // Simular POST request
  console.log('🚀 AUTOSAVE - POST Request Simulado:');
  console.log('📡 URL: POST /api/estimation/autosave');
  console.log('📅 Timestamp:', timestamp);
  console.log('📊 Data:', JSON.stringify(postData, null, 2));
  console.log('✅ Autosave completado exitosamente');
  console.log('---');
}

/**
 * Debounce function para evitar demasiadas llamadas
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Hook para autosave con debounce
 */
export function useAutosave(data: any, delay: number = 2000): void {
  const debouncedSave = debounce(simulatePost, delay);

  // Simular el efecto de autosave
  if (data && Object.keys(data).length > 0) {
    debouncedSave(data);
  }
}
